/**
 * Petits blocs partagés par les sections de la vitrine.
 *
 * La vitrine n’a pas de composants métier : elle a un rythme (une section, un
 * surtitre, un titre, un chapeau) répété huit fois. Ces éléments le portent,
 * pour que chaque section ne contienne plus que son contenu.
 *
 * Tout passe par les jetons du produit : `rideau-*`, `slate-*`, `rounded-carte`.
 */

import type { ReactNode } from 'react'

import { cn } from '../../lib/cn'
import { ImageAgrandissable } from './ImageAgrandissable'
import type { Visuel } from './visuels'

/** Largeur de lecture confortable — ~66 caractères, comme dans le back-office. */
export const MESURE = 'max-w-[66ch]'

export function Conteneur({
  className,
  children,
}: {
  readonly className?: string | undefined
  readonly children: ReactNode
}) {
  return (
    <div className={cn('mx-auto w-full max-w-[68rem] px-5 sm:px-8 lg:px-10', className)}>
      {children}
    </div>
  )
}

export interface SectionProps {
  /** Alterne fond blanc / fond de page (`slate-50`) pour rythmer le défilement. */
  readonly fond?: 'page' | 'blanc' | undefined
  /** La première section n’a pas de filet supérieur : l’en-tête le porte déjà. */
  readonly premiere?: boolean | undefined
  readonly libelleTitre: string
  readonly className?: string | undefined
  readonly children: ReactNode
}

export function Section({
  fond = 'page',
  premiere = false,
  libelleTitre,
  className,
  children,
}: SectionProps) {
  return (
    <section
      aria-labelledby={libelleTitre}
      className={cn(
        'py-14 sm:py-20 lg:py-24',
        premiere ? '' : 'border-t border-slate-200',
        fond === 'blanc' ? 'bg-white' : 'bg-papier',
        className,
      )}
    >
      <Conteneur>{children}</Conteneur>
    </section>
  )
}

export function Surtitre({
  ton = 'accent',
  children,
}: {
  readonly ton?: 'accent' | 'neutre' | undefined
  readonly children: ReactNode
}) {
  return (
    <p
      className={cn(
        'text-sm font-semibold tracking-[0.11em] uppercase',
        ton === 'accent' ? 'text-rideau-700' : 'text-slate-500',
      )}
    >
      {children}
    </p>
  )
}

export interface EnteteSectionProps {
  readonly surtitre: string
  readonly tonSurtitre?: 'accent' | 'neutre' | undefined
  readonly titre: ReactNode
  /** Identifiant repris par `aria-labelledby` de la section. */
  readonly id: string
  readonly chapeau?: ReactNode | undefined
  /**
   * ★ `sombre` sur une `SectionSombre` — trois couleurs à basculer, et pas une
   *   de moins : le surtitre, le titre et le chapeau. Un chapeau `slate-600`
   *   oublié sur fond `nuit-950` tombe à 2,2:1, sous le seuil AA, et c'est
   *   précisément le texte qu'on lit en diagonale.
   */
  readonly ton?: 'clair' | 'sombre' | undefined
  /** Le numéro d'acte, posé au-dessus du surtitre. Purement décoratif. */
  readonly acte?: string | undefined
}

export function EnteteSection({
  surtitre,
  tonSurtitre = 'accent',
  titre,
  id,
  chapeau,
  ton = 'clair',
  acte,
}: EnteteSectionProps) {
  const sombre = ton === 'sombre'
  return (
    <div className={MESURE}>
      {acte !== undefined && <NumeroActe numero={acte} ton={ton} />}
      <p
        className={cn(
          'text-[0.8125rem] font-semibold tracking-[0.2em] uppercase',
          acte !== undefined && 'mt-3',
          sombre
            ? 'text-rideau-300'
            : tonSurtitre === 'accent'
              ? 'text-rideau-700'
              : 'text-slate-500',
        )}
      >
        {surtitre}
      </p>
      <h2
        id={id}
        className={cn(
          'mt-4 text-[1.75rem] leading-[1.08] font-semibold tracking-[-0.025em] sm:text-[2.125rem] lg:text-[2.5rem]',
          sombre ? 'text-white' : 'text-slate-900',
        )}
      >
        {titre}
      </h2>
      {chapeau !== undefined && (
        <p
          className={cn(
            'mt-4 text-base sm:text-[1.0625rem]',
            sombre ? 'text-slate-300' : 'text-slate-600',
          )}
        >
          {chapeau}
        </p>
      )}
    </div>
  )
}

/** Filet d’accent — la seule tache de rouge de l’ouverture. */
export function FiletAccent() {
  return <div aria-hidden="true" className="h-[3px] w-13 rounded-sm bg-rideau-700" />
}

export function FlecheDroite({ className }: { readonly className?: string | undefined }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="15"
      height="15"
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0', className)}
    >
      <path
        d="M3 8h9M8.5 4.5 12 8l-3.5 3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Liste à puces des sections « duo » : une pastille rideau, un texte en mesure. */
export function ListePuces({ children }: { readonly children: ReactNode }) {
  return <ul className="mt-4 grid gap-2.5">{children}</ul>
}

export function Puce({ children }: { readonly children: ReactNode }) {
  return (
    <li className="relative pl-5 text-[0.9375rem] text-slate-600">
      <span
        aria-hidden="true"
        className="absolute top-[0.6rem] left-0 size-1.5 rounded-full bg-rideau-300"
      />
      {children}
    </li>
  )
}

/** Note de bas de section : la nuance qu’on ne veut pas cacher. */
export function Note({
  ton = 'clair',
  children,
}: {
  readonly ton?: 'clair' | 'sombre' | undefined
  readonly children: ReactNode
}) {
  return (
    <p
      className={cn(
        'mt-4 text-[0.9375rem]',
        ton === 'sombre' ? 'text-slate-400' : 'text-slate-500',
        MESURE,
      )}
    >
      {children}
    </p>
  )
}

/* -------------------------------------------------------------------------- */
/* Les captures — ce que la refonte ajoute de plus lourd à la page            */
/* -------------------------------------------------------------------------- */

/**
 * Une capture de l'application, encadrée et légendée.
 *
 * Quatre exigences tiennent dans ce composant, et aucune n'est décorative :
 *
 *  · **`width`/`height` explicites**, repris des dimensions RÉELLES du fichier
 *    (`visuels.ts`). Le navigateur réserve alors la place exacte avant l'octet
 *    d'image, et la page ne saute pas sous les yeux du lecteur ;
 *  · **`loading="lazy"` par défaut**, sauf la capture du premier écran — c'est
 *    tout l'objet de `prioritaire`. Une page de vente qui montre huit écrans ne
 *    doit pas en télécharger huit avant d'afficher son titre ;
 *  · **le texte alternatif DÉCRIT l'image** : il vient du catalogue, où il est
 *    écrit une fois et vérifié par un test ;
 *  · **le cadre est un `<figure>`**, avec sa `<figcaption>`. Une légende posée
 *    dans un `<p>` voisin n'est rattachée à rien pour qui écoute la page.
 *
 * ★ L'image elle-même est déléguée à `ImageAgrandissable` : elle se clique et
 * s'ouvre en grand, au clavier comme à la souris. Les quatre exigences
 * ci-dessus y sont tenues à l'identique — c'est la même balise `<img>`, à un
 * bouton près.
 */
export function Capture({
  visuel,
  prioritaire = false,
  className,
  classeImage,
}: {
  readonly visuel: Visuel
  /** `true` pour la seule capture du premier écran : elle se charge tout de suite. */
  readonly prioritaire?: boolean | undefined
  readonly className?: string | undefined
  readonly classeImage?: string | undefined
}) {
  return (
    <figure
      className={cn(
        /**
         * ★★ LA LÉGENDE COLLE À L'IMAGE — retour du client du 25/08, capture à
         * l'appui : « fais en sorte que la légende de l'image soit directement
         * sous l'image et qu'il n'y ait pas tout cet espace sous l'image ».
         *
         * Il y avait ici `flex h-full flex-col`, avec `mt-auto` sur la légende.
         * L'intention était bonne : deux captures côte à côte n'ont jamais le
         * même rapport, et aligner leurs pieds évitait une rangée en dents de
         * scie. Le prix, lui, n'avait pas été regardé : dès qu'une capture est
         * SEULE dans sa rangée, ou simplement plus courte que sa voisine, la
         * figure s'étire quand même et `mt-auto` pousse la légende tout en bas.
         * Résultat, une bande blanche de plusieurs centaines de pixels entre
         * l'écran montré et la phrase qui l'explique — exactement ce qu'une
         * légende ne doit pas être : loin de ce qu'elle légende.
         *
         * ★ L'arbitrage : la PROXIMITÉ prime sur l'alignement. Une rangée dont
         *   les cartes finissent à des hauteurs différentes se lit très bien ;
         *   une légende orpheline au bas d'un vide, non. La figure prend donc
         *   sa hauteur naturelle, et les grilles qui l'accueillent alignent
         *   leurs cartes par le HAUT (`items-start`), ce qui est de toute façon
         *   la lecture naturelle d'une page.
         */
        'm-0 overflow-hidden rounded-carte border border-slate-200 bg-white',
        className,
      )}
    >
      <ImageAgrandissable visuel={visuel} prioritaire={prioritaire} classeImage={classeImage} />
      {visuel.legende !== undefined && (
        <figcaption className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 sm:px-5">
          {visuel.legende}
        </figcaption>
      )}
    </figure>
  )
}

/** Une pastille numérotée posée sur une capture, en pourcentage de l'image. */
export interface Pastille {
  readonly numero: number
  /** Position du centre, en pourcentage de la largeur de l'image. */
  readonly x: number
  /** Position du centre, en pourcentage de la hauteur de l'image. */
  readonly y: number
  /** Ce que la pastille désigne — lu par qui n'a pas l'image. */
  readonly libelle: string
}

/**
 * Une capture SURMONTÉE de pastilles numérotées — le visuel central de la page.
 *
 * ★ **Les pastilles sont posées PAR-DESSUS l'image, jamais incrustées dans le
 * PNG.** La raison est mécanique : les captures se régénèrent d'une seule
 * commande (`e2e/captures/capturer.ts`), et une annotation cuite dans le
 * fichier ne survivrait pas à la première régénération — elle se retrouverait
 * décalée, à désigner le vide, sans que rien ne le signale. Posées ici, elles
 * se déplacent en changeant deux nombres.
 *
 * Les pastilles sont `aria-hidden` : leur libellé est déjà écrit dans le texte
 * de la section, et l'entendre deux fois ferait une liste de chiffres sans
 * contexte. Il est malgré tout porté ici, parce que c'est ici qu'on relit la
 * position — un numéro qui désigne le vide se repère en lisant ce qu'il
 * prétend désigner.
 */
export function CaptureAnnotee({
  visuel,
  pastilles,
  prioritaire = false,
  className,
}: {
  readonly visuel: Visuel
  readonly pastilles: readonly Pastille[]
  readonly prioritaire?: boolean | undefined
  readonly className?: string | undefined
}) {
  return (
    <figure
      className={cn(
        'relative m-0 overflow-hidden rounded-carte border border-slate-200 bg-white',
        className,
      )}
    >
      <div className="relative">
        <ImageAgrandissable visuel={visuel} prioritaire={prioritaire} />
        {pastilles.map((pastille) => (
          <span
            key={pastille.numero}
            aria-hidden="true"
            style={{ left: `${String(pastille.x)}%`, top: `${String(pastille.y)}%` }}
            /* `pointer-events-none` : les pastilles flottent AU-DESSUS de
               l'image, qui est désormais un bouton. Sans cela, les deux seuls
               points de l'image qu'on a envie de regarder de près seraient
               précisément les deux qui ne s'ouvrent pas. */
            className="pointer-events-none absolute flex size-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-rideau-700 text-sm font-semibold text-white ring-3 ring-white sm:size-9 sm:text-base"
          >
            {pastille.numero}
          </span>
        ))}
      </div>
      {visuel.legende !== undefined && (
        <figcaption className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 sm:px-5">
          {visuel.legende}
        </figcaption>
      )}
    </figure>
  )
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  L'AFFICHE — les primitives propres à la vitrine                           */
/* ══════════════════════════════════════════════════════════════════════════ */

/**
 * Une section sur fond de salle éteinte.
 *
 * Le noir n'est pas un effet : c'est ce qui donne un RYTHME à une page longue.
 * Dix-sept sections blanches à la suite se lisent comme un document ; trois
 * respirations sombres — l'ouverture, la campagne, le tarif — découpent le
 * récit en actes et disent où l'on regarde.
 *
 * `text-slate-100` est posé ici et non hérité : sur fond `nuit-950`, un texte
 * `slate-600` (le défaut de la page) tomberait à 2,2:1.
 */
export function SectionSombre({
  libelleTitre,
  poursuite = true,
  className,
  children,
}: {
  readonly libelleTitre: string
  readonly poursuite?: boolean | undefined
  readonly className?: string | undefined
  readonly children: ReactNode
}) {
  return (
    <section
      aria-labelledby={libelleTitre}
      className={cn(
        'relative isolate overflow-hidden bg-nuit-950 text-slate-100',
        poursuite && 'affiche-poursuite',
        className,
      )}
    >
      {/* La trame de fauteuils, en couche de fond. `text-white/[0.07]` sert de
          `currentColor` au dégradé radial de l'utilitaire. */}
      <div
        aria-hidden="true"
        className="affiche-trame pointer-events-none absolute inset-0 -z-10 text-white/[0.08]"
      />
      <Conteneur className="relative py-16 sm:py-20 lg:py-24">{children}</Conteneur>
    </section>
  )
}

/**
 * Le numéro d'un acte, en gros, à la manière d'une affiche.
 *
 * Décoratif au sens strict — le titre qui suit porte tout le sens — donc
 * `aria-hidden`. Une lectrice d'écran n'a pas à entendre « zéro trois ».
 */
export function NumeroActe({
  numero,
  ton = 'clair',
}: {
  readonly numero: string
  readonly ton?: 'clair' | 'sombre' | undefined
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'block font-mono text-[2.75rem] leading-none font-light tracking-tighter tabular-nums sm:text-[3.25rem]',
        ton === 'sombre' ? 'text-white/25' : 'text-rideau-200',
      )}
    >
      {numero}
    </span>
  )
}

/**
 * ★ LA SOUCHE — la carte-signature de la page.
 *
 * Un rectangle blanc dont le bas se détache par une ligne de perforation, avec
 * ses deux encoches. C'est le seul ornement de la vitrine, et il est choisi
 * pour une raison : c'est l'objet que ce logiciel fabrique. Une page de
 * billetterie qui ressemble à un billet se retient ; une page de billetterie
 * faite de rectangles arrondis ressemble à toutes les autres.
 *
 * `pied` est la partie détachable — le chiffre, la précision, ce qu'on garde.
 */
export function Souche({
  titre,
  children,
  pied,
  className,
}: {
  readonly titre: ReactNode
  readonly children: ReactNode
  readonly pied?: ReactNode | undefined
  readonly className?: string | undefined
}) {
  return (
    <article
      className={cn(
        'affiche-souche flex flex-col bg-white ring-1 ring-slate-200 ring-inset',
        className,
      )}
      style={{ ['--pos-souche' as string]: 'calc(100% - 4.25rem)' }}
    >
      <div className="flex-1 px-5 py-6 sm:px-6">
        <h3 className="text-[1.0625rem] font-semibold tracking-tight text-slate-900">{titre}</h3>
        <div className="mt-2 text-[0.9375rem] text-slate-600">{children}</div>
      </div>
      {pied !== undefined && (
        <>
          <div aria-hidden="true" className="affiche-perforation h-px text-slate-400" />
          <div className="px-5 py-3.5 text-sm text-slate-500 sm:px-6">{pied}</div>
        </>
      )}
    </article>
  )
}

/**
 * L'étiquette d'un fait vérifiable — un chiffre et ce qu'il compte.
 * Pas de superlatif : un nombre, et le nom de ce qu'il dénombre.
 */
export function Chiffre({
  valeur,
  libelle,
  ton = 'clair',
}: {
  readonly valeur: string
  readonly libelle: string
  readonly ton?: 'clair' | 'sombre' | undefined
}) {
  return (
    <div className={cn('border-l pl-4', ton === 'sombre' ? 'border-white/20' : 'border-slate-200')}>
      <p
        className={cn(
          'text-2xl font-semibold tracking-tight tabular-nums sm:text-[1.75rem]',
          ton === 'sombre' ? 'text-white' : 'text-slate-900',
        )}
      >
        {valeur}
      </p>
      <p className={cn('mt-1 text-sm', ton === 'sombre' ? 'text-slate-300' : 'text-slate-500')}>
        {libelle}
      </p>
    </div>
  )
}
